import { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  Box, Container, AppBar, Toolbar, IconButton,
  Typography, CircularProgress, Alert,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  MenuBook as KnowledgeIcon
} from '@mui/icons-material';
import { useGetBusinessByIdQuery } from '../../redux/thunks/unauth/getbusinessbyid';
import BusinessDocumentUpload from '../ProviderProfile/BusinessDocumentUpload';

const DOCUMENT_QUOTAS = {
  free: {
    maxDocuments:    1,
    maxFileSize:     5  * 1024 * 1024,
    maxTotalStorage: 5  * 1024 * 1024
  },
  premium: {
    maxDocuments:    20,
    maxFileSize:     50  * 1024 * 1024,
    maxTotalStorage: 500 * 1024 * 1024
  }
};

export default function BusinessKnowledgeBasePage() {
  const { businessId } = useParams();
  const navigate       = useNavigate();
  const location       = useLocation();
  const theme          = useTheme();
  const isMobile       = useMediaQuery(theme.breakpoints.down('md'));

  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const token    = useSelector(state => state.auth?.token    || null);
  const account  = useSelector(state => state.auth?.user?.accountDetails || null);

  const businessName = location.state?.businessName || 'Your Business';
  const returnPath   = location.state?.returnPath   || null;

  // Derive title slug the same way BusinessDetails does
  const title = useMemo(() =>
    businessName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  [businessName]);

  const isAuthenticated = authStatus === 'authenticated' && token;

  const accountType = account?.type || 'free';
  const quotas      = DOCUMENT_QUOTAS[accountType] || DOCUMENT_QUOTAS.free;

  const { data: profile, isLoading: profileLoading } = useGetBusinessByIdQuery(
    { businessId, authType: 'none', token, title },
    { skip: !businessId }
  );

  const existingDocuments = profile?.documents || [];

  const handleBack = () => {
    if (returnPath) navigate(returnPath);
    else navigate(-1);
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>

      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
          <KnowledgeIcon color="primary" sx={{ mr: 1 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Business Knowledge Base
            </Typography>
            {!isMobile && (
              <Typography variant="caption" color="text.secondary">
                {businessName}
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>

        <Alert severity="info" sx={{ mb: 3 }}>
          Upload menus, documents, and photos so your AI assistant can answer
          customer questions accurately about your business.
        </Alert>

        {profileLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <BusinessDocumentUpload
            businessId={businessId}
            businessName={businessName}
            existingDocuments={existingDocuments}
            onSuccess={() => navigate(returnPath || -1)}
            onCancel={handleBack}
            quotas={quotas}
            accountType={accountType}
          />
        )}

      </Container>
    </Box>
  );
}